class IconsPlusSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector(".splide");
    this.initialized = false;
    this.enableSliderDesktop = this.dataset.enableSliderDesktop === "true";
    this.enableSliderMobile = this.dataset.enableSliderMobile === "true";
    this.container = this.parentElement.querySelector(
      ".icons-plus__container",
    );

    // Listen for Shopify theme editor events
    document.addEventListener("shopify:section:load", (event) => {
      if (this.isRelevantSection(event)) {
        this.initSlider();
      }
    });

    document.addEventListener("shopify:block:select", (event) => {
      if (this.isRelevantBlock(event)) {
        this.initSlider();
      }
    });
  }

  connectedCallback() {
    this.handleResponsiveSlider();
    this.addResizeListener();
  }

  isRelevantSection(event) {
    return this.dataset.sectionId === event.detail.sectionId;
  }

  isRelevantBlock(event) {
    return (
      this.dataset.sectionId === event.detail.sectionId &&
      this.dataset.blockId === event.detail.blockId
    );
  }

  isSliderActive() {
    const isMobile = window.innerWidth <= 749;
    return isMobile ? this.enableSliderMobile : this.enableSliderDesktop;
  }

  handleResponsiveSlider() {
    if (this.isSliderActive()) {
      this.showSlider();
      this.initSlider();
    } else {
      this.hideSlider();
      this.showContainer();
    }
  }

  showSlider() {
    if (this.slider) {
      this.slider.parentElement.style.display = "block";
    }
  }

  hideSlider() {
    if (this.slider) {
      this.slider.parentElement.style.display = "none";
    }
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.initialized = false;
    }
  }

  showContainer() {
    if (this.container) {
      this.container.style.display = "flex";
    }
  }

  hideContainer() {
    if (this.container) {
      this.container.style.display = "none";
    }
  }

  addResizeListener() {
    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResponsiveSlider();
      }, 150);
    });
  }

  initSlider() {
    // If Splide is not loaded or slider element doesn't exist, don't proceed
    if (typeof Splide === "undefined" || !this.slider) {
      return;
    }

    // If already initialized, destroy the previous instance
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.initialized = false;
    }

    // Only proceed if slider should be active
    if (!this.isSliderActive()) {
      return;
    }

    this.hideContainer();

    // Get configuration from data attributes
    const config = this.getConfig();

    // Initialize Splide
    this.splideInstance = new Splide(this.slider, config);

    // Set up event handlers
    this.setupEventHandlers();

    // Mount the slider
    this.splideInstance.mount();

    this.initialized = true;
  }

  getConfig() {
    const config = {
      type: "slide",
      perPage: parseInt(this.dataset.slidesDesktop) || 3,
      gap: parseInt(this.dataset.gap) || 20,
      padding: parseInt(this.dataset.padding) || 30,
      arrows: this.dataset.arrows !== "false" ? ".splide__arrow" : false, // Use our custom arrows
      pagination: this.dataset.pagination !== "false",
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      rewind: this.dataset.rewind === "true",
      autoplay: this.dataset.autoplay === "true",
      interval:
        this.dataset.autoplay === "true"
          ? parseInt(this.dataset.autoplaySpeed || 5) * 1000
          : 0,
      breakpoints: {
        749: {
          perPage: parseInt(this.dataset.slidesMobile) || 1,
        },
      },
    };

    return config;
  }

  setupEventHandlers() {
    // Handle slide change events
    this.splideInstance.on("moved", () => {
      this.handleSlideChange();
    });

    this.splideInstance.on("mounted", () => {
      this.handleSlideChange();

      // Equalize heights after a short delay to ensure all content is rendered
      setTimeout(() => {
        this.equalizeSlideHeights();
      }, 100);

      // Add resize handler for responsive behavior
      window.addEventListener(
        "resize",
        this.debounce(() => {
          this.handleSlideChange();
          this.equalizeSlideHeights();
        }, 200),
      );
    });
  }

  equalizeSlideHeights() {
    // Reset heights first
    const slides = this.splideInstance.Components.Elements.slides;
    slides.forEach((slide) => {
      const item = slide.querySelector(".icons-plus__item");
      if (item) {
        item.style.height = "";
        const inner = item.querySelector(".icons-plus__content");
        if (inner) inner.style.height = "";
      }
    });

    // Get the tallest height
    let maxHeight = 0;
    slides.forEach((slide) => {
      const item = slide.querySelector(".icons-plus__item");
      if (item) {
        const height = item.offsetHeight;
        maxHeight = Math.max(maxHeight, height);
      }
    });

    // Apply the height to all slides
    if (maxHeight > 0) {
      slides.forEach((slide) => {
        const item = slide.querySelector(".icons-plus__item");
        if (item) {
          item.style.height = `${maxHeight}px`;
          item.classList.add("js-equal-height");
        }
      });
    }
  }

  handleSlideChange() {
    const activeSlide =
      this.splideInstance.Components.Elements.slides[this.splideInstance.index];
    if (activeSlide) {
      // Remove active class from all slides
      this.splideInstance.Components.Elements.slides.forEach((slide) => {
        slide.classList.remove("is-active-slide");
      });

      // Add active class to current slide
      activeSlide.classList.add("is-active-slide");

      // Hide pagination and arrows if all slides fit in the view
      this.updateNavigationVisibility();
    }
  }

  updateNavigationVisibility() {
    // Check if all slides fit in the view without scrolling
    const Components = this.splideInstance.Components;
    const totalSlides = Components.Elements.slides.length;
    const perPage = this.splideInstance.options.perPage;
    const shouldHideNavigation = totalSlides <= perPage;

    // Get pagination and arrow elements from Splide's structure
    const pagination = Components.Elements.root.querySelector(
      ".splide__pagination",
    );
    const arrows = Components.Elements.root.querySelectorAll(".splide__arrow");

    // Hide/show pagination based on whether all slides fit
    if (pagination) {
      pagination.classList.toggle("hidden", shouldHideNavigation);
    }

    // Hide/show arrows based on whether all slides fit
    if (arrows && arrows.length) {
      arrows.forEach((arrow) => {
        arrow.classList.toggle("hidden", shouldHideNavigation);
      });
    }
  }

  // Utility function for debouncing resize events
  debounce(func, wait) {
    let timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this), wait);
    };
  }
}

// Register the custom element
customElements.define(
  "icons-plus-slider",
  IconsPlusSlider,
);
